import { Chip, Tooltip } from "@material-ui/core";
import { Fragment, type CSSProperties, type ReactElement } from "react";

import type { Assignment } from "../../api/types";

/**
 * Props for AssignmentBadges. Exactly one of `assignment` or the
 * explicit props should be provided depending on context.
 */
type AssignmentBadgesProps =
  | {
      /** Assignment object (from API response) — source/conflicts read from it. */
      assignment: Assignment;
      inherited?: never;
    }
  | {
      assignment?: never;
      /** Source label for inherited assignments (lookup context). */
      inherited: true;
      /** Optional conflicts from the lookup item context. */
      conflicts?: Assignment[];
    };

const chipStyles: Record<string, CSSProperties> = {
  ui: {
    backgroundColor: "#4caf50",
    color: "#fff",
  },
  catalog: {
    backgroundColor: "#2196f3",
    color: "#fff",
  },
  inherited: {
    backgroundColor: "transparent",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    color: "rgba(0, 0, 0, 0.87)",
  },
  conflict: {
    backgroundColor: "#f44336",
    color: "#fff",
  },
};

/**
 * Reusable badges for assignment source and conflict status.
 *
 * - "Catalog" chip (blue) for `catalog-info.yaml` annotations.
 * - "Inherited" chip (teal) for inherited account assignments.
 * - "Conflict" chip (red) when the same nodeRef is claimed by >1 entity.
 *   Tooltip lists the conflicting entityRefs and their sources.
 */
export function AssignmentBadges(
  props: AssignmentBadgesProps,
): ReactElement | null {
  // Resolve source
  let source: string | undefined;
  let resolvedConflicts: Assignment[] | undefined;

  if ("assignment" in props && props.assignment) {
    source = props.assignment.source;
    resolvedConflicts = props.assignment.conflicts;
  } else if ("inherited" in props && props.inherited) {
    source = "inherited";
    resolvedConflicts = props.conflicts;
  }

  if (!source && (!resolvedConflicts || resolvedConflicts.length === 0)) {
    return null;
  }

  const chips: ReactElement[] = [];

  if (source === "catalog") {
    chips.push(
      <Chip
        key={source}
        size="small"
        label="Catalog"
        style={chipStyles.catalog}
      />,
    );
  }

  if (source === "inherited") {
    chips.push(
      <Chip
        key="inherited"
        size="small"
        label="Inherited"
        style={chipStyles.inherited}
      />,
    );
  }

  if (resolvedConflicts && resolvedConflicts.length > 0) {
    const conflictList = resolvedConflicts
      .map((c) => `"${c.entityRef}" (${c.source ?? "?"})`)
      .join("\n");

    chips.push(
      <Tooltip
        key="conflict"
        title={
          <Fragment>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Conflicting assignments:
            </div>
            <div style={{ whiteSpace: "pre-line" }}>{conflictList}</div>
          </Fragment>
        }
      >
        <Chip size="small" label="Conflict" style={chipStyles.conflict} />
      </Tooltip>,
    );
  }

  if (chips.length === 0) return null;

  return (
    <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
      {chips}
    </span>
  );
}
